import { useState } from 'react'
import { Alert, Button, Descriptions, Input, Modal, Space, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import {
  closePeriod,
  fetchJournalEntry,
  isPeriodClosed,
  type JournalEntryLine,
  type Period,
} from './financeApi'
import { periodRangeLabel } from './periodFormat'

interface PeriodCloseModalProps {
  /** `null` — oyna yopiq */
  period: Period | null
  /**
   * Barcha davrlar — "oldingi davr ochiq" holatini OLDINDAN aytish uchun.
   *
   * ⚠️ `null` = ro'yxat ISHONCHSIZ (yuklanmadi yoki yiqildi). U holda
   * hech qanday xulosa chiqarilmaydi: yiqilgan so'rovdan "oldingi
   * davrlar yopilgan" degan xulosa chiqarish `guards.mjs` 3-bo'limidagi
   * aynan nuqson bo'lardi (`!isPending` ≠ `isSuccess`).
   */
  periods: readonly Period[] | null
  onClose: () => void
}

/**
 * <b>"Avgustni yop"</b> — bu paneldagi eng xavfli tugma.
 *
 * <h2>⚠️ NIMA BO'LADI</h2>
 * Amal `xsib-ledger` dan agregat oladi va BITTA yig'ma provodka yozadi
 * (`QARORLAR.md` §20.2). Undan keyin davr O'ZGARMAS: qayta ochish YO'Q,
 * provodka o'chirilmaydi (`finance.journal_immutable()`).
 *
 * <h2>⚠️ IDEMPOTENTLIK KALITI SERVERDA — PANEL YUBORMAYDI</h2>
 * Tekshirildi: `PeriodController` da `Idempotency-Key` sarlavhasi YO'Q,
 * kalit `PeriodServiceImpl#closeKey` da davrning o'z id'sidan quriladi.
 * Bu odatiy naqshdan ONGLI chetlanish — klient kalit bersa, ikkinchi
 * bosishda brauzer YANGI kalit yuborardi va §20.9-D dagi «ikki bosish,
 * ikki provodka» aynan qolardi.
 *
 * <p>⚠️ Lekin server kafolati panelni ishdan ozod QILMAYDI: ikki marta
 * bosish baribir ikki so'rov, ikki javob va operatorda ikkilanish
 * degani. Shuning uchun oyna ham to'sadi — `confirmLoading` va tugmadagi
 * `disabled` (ikkalasi ham, chunki `confirmLoading` faqat OK tugmasiga
 * tegadi va bekor qilish tugmasi ochiq qolardi).
 *
 * <h2>⚠️ TAKROR — MUVAFFAQIYAT, LEKIN YASHIRILMAYDI</h2>
 * Server takroriy bosishda `200` va `replayed: true` qaytaradi. Oyna
 * buni SARIQ (`warning`) bilan ko'rsatadi: bu xato emas, lekin
 * "yopildi" ham emas — "allaqachon yopilgan edi, YANGI PROVODKA
 * YOZILMADI" (`CLAUDE.md` §3.2, 4-qoida: hech qachon "muvaffaqiyatli"
 * deb yolg'on aytmang).
 *
 * <h2>⚠️ NATIJA KO'RSATILADI — "bajarildi" YETARLI EMAS</h2>
 * Operator nima yozilganini bilishi kerak: nechta satr, QAYSI
 * hisoblarga va ledger QAYSI LAHZADAGI holatni bergan (`sourceAsOf`,
 * §18 3-band). Shuning uchun oyna yopilmaydi — u natija ko'rinishiga
 * o'tadi.
 */
export function PeriodCloseModal({ period, periods, onClose }: PeriodCloseModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  /**
   * ⚠️ HIMOYA DARAJASI TESKARI TAQSIMLANGAN EDI (UX auditi, 4.7):
   * davrni O'CHIRISH (faqat bo'sh va ochiq davr o'chadi, yo'qotish yo'q)
   * `modal.confirm` so'rardi, davrni YOPISH esa — paneldagi eng
   * qaytarilmaydigan amal, u ledger'ga yig'ma provodka YOZADI — hech
   * qanday terish talab qilmasdi. Endi yopish `NasDeleteModal` va
   * `CancelPaymentModal` bilan bir darajada: davr kodini QO'LDA terish.
   */
  const [typed, setTyped] = useState('')

  const closing = useMutation({
    mutationFn: (id: string) => closePeriod(id),
    onSuccess: () => {
      // ⚠️ `finance.all` PREFIKS: davr holati ham, jurnal ham eskirdi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    },
  })

  const result = closing.isSuccess ? closing.data : null

  /**
   * Yozilgan provodka — "qaysi hisoblarga" savoliga javob.
   *
   * ⚠️ `404` KUTILADIGAN javob: yig'ma provodkaning satrlari
   * TAQSIMLANMAGAN (`orgNodeId = null`) va ular `SUBTREE` doirasiga
   * TUSHMAYDI (`JournalRepository` javadoc'i). Ya'ni filial boshlig'i
   * davrni yopsa, provodkani ko'ra olmaydi. Bu nuqson emas — lekin
   * "topilmadi" deb ko'rsatish YOLG'ON bo'lardi, shuning uchun quyida
   * alohida matn bor.
   */
  const entry = useQuery({
    queryKey: queryKeys.finance.entry(result?.entryId ?? ''),
    queryFn: ({ signal }) => fetchJournalEntry(result?.entryId ?? '', signal),
    enabled: result?.entryId != null,
    retry: false,
  })

  const dismiss = () => {
    closing.reset()
    setTyped('')
    onClose()
  }

  const confirmed = period !== null && typed.trim() === period.code

  /**
   * Oldindan aytiladigan to'siq — davrlar TARTIB bilan yopiladi.
   *
   * Server ham rad etadi (`PeriodServiceImpl` → `findEarlierOpen`),
   * lekin sabab bilan birga OLDINDAN ko'rsatilsa operator nima
   * qilishini biladi: avval oldingi oyni yopadi.
   */
  const earlierOpen =
    period === null || periods === null
      ? null
      : (periods
          .filter((item) => !isPeriodClosed(item) && item.startsAt < period.startsAt)
          .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0] ?? null)

  const blocked = earlierOpen !== null

  const lineColumns: ColumnsType<JournalEntryLine> = [
    {
      key: 'account',
      title: t('finance.entry.account'),
      render: (_, line) => (
        <Space size={6} wrap>
          <Typography.Text code>{line.accountCode}</Typography.Text>
          <Typography.Text>{seededName('accounts', line.accountCode, line.accountName, t)}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'debit',
      title: t('finance.entry.debit'),
      width: 150,
      align: 'right',
      // ⚠️ Ishora KELISHUV bo'yicha o'qiladi (manfiy = debit), panel uni
      // AG'DARMAYDI — faqat ikki ustunga ajratadi. Buxgalter aynan shu
      // ko'rinishni kutadi.
      render: (_, line) =>
        line.amount < 0
          ? formatMoneyValue(money(-line.amount, entry.data?.currency ?? ''))
          : null,
    },
    {
      key: 'credit',
      title: t('finance.entry.credit'),
      width: 150,
      align: 'right',
      render: (_, line) =>
        line.amount > 0 ? formatMoneyValue(money(line.amount, entry.data?.currency ?? '')) : null,
    },
  ]

  return (
    <Modal
      open={period !== null}
      title={t('finance.close.title', { code: period?.code ?? '' })}
      okText={t('finance.close.confirm')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true, disabled: closing.isPending || blocked || !confirmed }}
      cancelButtonProps={{ disabled: closing.isPending }}
      confirmLoading={closing.isPending}
      // ⚠️ Amal ketayotganda oyna tasodifan yopilmasin: fon bosilishi
      // so'rovni to'xtatmaydi, faqat natijani ekrandan olib ketardi.
      maskClosable={!closing.isPending}
      closable={!closing.isPending}
      onCancel={dismiss}
      onOk={() => {
        if (period === null || blocked || !confirmed) return
        closing.mutate(period.id)
      }}
      destroyOnHidden
      width={720}
      footer={
        result === null
          ? undefined
          : [
              <Button key="done" type="primary" onClick={dismiss}>
                {t('common.close')}
              </Button>,
            ]
      }
    >
      {result === null ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={t('finance.close.warnTitle')}
            description={t('finance.close.warnBody')}
          />

          {blocked && earlierOpen ? (
            <Alert
              type="error"
              showIcon
              message={t('finance.close.earlierOpenTitle')}
              description={t('finance.close.earlierOpenBody', { code: earlierOpen.code })}
            />
          ) : null}

          {period ? (
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('finance.period.code')}>
                <Typography.Text strong>{period.code}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('finance.period.range')}>
                {periodRangeLabel(period)}
              </Descriptions.Item>
              {/* ⚠️ ANIQ LAHZALAR ham ko'rsatiladi: `Asia/Kabul` = UTC+4:30
                  va oy chegarasi kunning boshida EMAS — buni yashirish
                  "nega 31-avgust kechqurungi to'lov sentyabrga tushdi?"
                  degan savolga javobsiz qoldirardi */}
              <Descriptions.Item label={t('finance.period.bounds')}>
                <Tooltip title={`${utcTooltip(period.startsAt)} — ${utcTooltip(period.endsAt)}`}>
                  <span>
                    {formatDateTime(period.startsAt)} — {formatDateTime(period.endsAt)}
                  </span>
                </Tooltip>
              </Descriptions.Item>
            </Descriptions>
          ) : null}

          {/* Xato OYNANING ICHIDA qoladi: server sababni MATN bilan
              aytadi ("Avval oldingi davrni yoping", "Hisoblar rejasida
              uyi yo'q ledger hisob turlari: ...") va aynan o'sha matn
              operatorga keyingi qadamni ko'rsatadi */}
          {closing.isError ? (
            <Alert type="error" showIcon message={translateError(closing.error, t)} />
          ) : null}

          {blocked ? null : (
            <div>
              <Typography.Text type="secondary">
                {t('actions.typeToConfirm', { value: period?.code ?? '' })}
              </Typography.Text>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={period?.code ?? ''}
                disabled={closing.isPending}
                style={{ marginTop: 8 }}
              />
            </div>
          )}

          <Typography.Text type="secondary">{t('finance.close.idempotencyNote')}</Typography.Text>
        </Space>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type={result.replayed ? 'warning' : 'success'}
            showIcon
            message={
              result.replayed ? t('finance.close.replayedTitle') : t('finance.close.doneTitle')
            }
            // Server matni faqat o'zbekcha; boshqa tilda tafsilot quyidagi
            // jadval va «pul harakati bo'lmadi» ogohlantirishida bor
            description={t('common.langCode') === 'uz' ? result.message : undefined}
          />

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('finance.close.lineCount')}>
              {result.replayed ? t('common.dash') : result.lineCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('finance.close.sourceRows')}>
              {result.replayed ? t('common.dash') : result.sourceRows}
            </Descriptions.Item>
            <Descriptions.Item label={t('finance.close.sourceAsOf')}>
              {result.sourceAsOf === null ? (
                t('common.dash')
              ) : (
                <Tooltip title={utcTooltip(result.sourceAsOf)}>
                  <span>{formatDateTime(result.sourceAsOf)}</span>
                </Tooltip>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Text type="secondary">{t('finance.close.asOfHint')}</Typography.Text>

          {result.entryId === null ? (
            <Alert type="info" showIcon message={t('finance.close.noPosting')} />
          ) : entry.isError ? (
            // ⚠️ "Topilmadi" DEB YOZILMAYDI: provodka BOR, u shu
            // operatorning doirasida ko'rinmaydi (taqsimlanmagan satrlar)
            <Alert
              type="warning"
              showIcon
              message={t('finance.close.linesHiddenTitle')}
              description={t('finance.close.linesHiddenBody')}
            />
          ) : (
            <Table<JournalEntryLine>
              columns={lineColumns}
              dataSource={entry.isSuccess ? [...entry.data.lines] : []}
              rowKey={(line) => line.id}
              loading={entry.isPending}
              size="small"
              pagination={false}
              title={() => t('finance.close.linesTitle')}
            />
          )}
        </Space>
      )}
    </Modal>
  )
}
